/**
 * CIO Facet Adapter — URL-driven replacement for hec-composable's CioFacetContext.
 *
 * In hec-composable, facet state lives in a React Context + useReducer, synced
 * to the URL via useSyncCioFacetsToUrl. Here, the URL IS the source of truth.
 * This adapter provides the same interface (CioFacetProvider, useCioFacetSelector)
 * but derives all state from useSearchParams().
 */
import {
  type Dispatch,
  type ReactNode,
  createContext,
  useCallback,
  useMemo,
} from "react";
// Need React for useContext
import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import {
  FALLBACK_FACET_MAPPING_CONFIG,
  type FacetMappingConfig,
  cioFacetToUrlParam,
  createFacetMappingConfig,
  isCioFacetParam,
  urlParamToCioFacet,
} from "~/hec/config/facet-mapping";
import { humanizeCategoryId } from "~/hec/utils/humanize";
import { serializeSearchParams } from "~/lib/utils/url";
import { trackPageEvent } from "~/segment/utils";

// ---------------------------------------------------------------------------
// Types (matching hec-composable cio-facet.tsx)
// ---------------------------------------------------------------------------

export type CioFacetOption = {
  status: string;
  count: number;
  display_name: string;
  value: string;
  data: object;
};

export type CioFacets = {
  display_name: string;
  name: string;
  type: string;
  options: CioFacetOption[];
  hidden: boolean;
  data: object;
};

export type SelectedCioFacets = {
  [category: string]: { value: string; display_name: string }[];
};

export type SortOption = {
  sort_by: string;
  display_name: string;
  sort_order: string;
  status?: string;
};

export type CioFacetState = {
  customSitePreferences: { data?: any[] };
  /**
   * The facet mapping (BM `Constructor_FacetMapping`) this listing was rendered
   * with, resolved server-side and threaded down by `CioFacetProvider`. Facet
   * surfaces MUST read it from here rather than importing a module constant, so
   * the drawer, the active-filter pills and the CIO query all gate against one
   * per-request value.
   */
  facetMappingConfig: FacetMappingConfig;
  selectedCioFacets: SelectedCioFacets;
  cioFacets: CioFacets[];
  totalResults: {
    responseTotal: number;
    selectedTotal: number;
  };
  sortOptions: SortOption[];
  selectedSortOption: SortOption | null;
  isInitialized: boolean;
};

// Action type constants (matching hec-composable)
export const SET_CIO_FACETS = "SET_CIO_FACETS";
export const RESET_CIO_FACETS = "RESET_CIO_FACETS";
export const SET_CIO_SELECTED_FACETS = "SET_CIO_SELECTED_FACETS";
export const SET_SELECTED_SORT_OPTION = "SET_SELECTED_SORT_OPTION";
export const SET_SORT_OPTIONS = "SET_SORT_OPTIONS";
export const SET_CUSTOM_SITE_PREFERENCES = "SET_CUSTOM_SITE_PREFERENCES";
export const SET_CIO_FACETS_INITIALIZED = "SET_CIO_FACETS_INITIALIZED";

export type CioFacetAction =
  | { type: typeof RESET_CIO_FACETS }
  | {
      type: typeof SET_CIO_FACETS;
      payload: { facets: CioFacets[]; total_num_results: number };
    }
  | { type: typeof SET_CIO_SELECTED_FACETS; payload: SelectedCioFacets }
  | { type: typeof SET_CUSTOM_SITE_PREFERENCES; payload: object }
  | { type: typeof SET_SORT_OPTIONS; payload: SortOption[] }
  | { type: typeof SET_SELECTED_SORT_OPTION; payload: SortOption | null }
  | { type: typeof SET_CIO_FACETS_INITIALIZED };

// ---------------------------------------------------------------------------
// Reserved URL params (not facets)
// ---------------------------------------------------------------------------

// Facet params are keyed by CIO facet name (e.g. `brand`, `productType`); the
// URL only ever carries facets that pass the same `ALLOWED_CIO_FACETS` gate the
// drawer renders from. Reading/clearing against that allowlist (rather than a
// reserved denylist) keeps non-facet params — AFID/afid/a_aid, utm_*, gclid,
// src — out of the selected-facet state and preserves them across facet changes
// and "clear all" instead of stripping the TPA/ad context (SFCC-11336).

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CioFacetContext = createContext<{
  state: CioFacetState;
  dispatch: Dispatch<CioFacetAction>;
}>({
  state: {
    customSitePreferences: {},
    facetMappingConfig: FALLBACK_FACET_MAPPING_CONFIG,
    selectedCioFacets: {},
    cioFacets: [],
    totalResults: { responseTotal: 0, selectedTotal: 0 },
    sortOptions: [],
    selectedSortOption: null,
    isInitialized: true,
  },
  dispatch: () => null,
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CioFacetProviderProps {
  facets: CioFacets[];
  sortOptions: SortOption[];
  totalResults: number;
  children: ReactNode;
  /**
   * BM `Constructor_FacetMapping` (display name → CIO facet name) from the PLP
   * / search loader — the facet allowlist + drawer labels for this site. Omit
   * only where no loader payload exists (unit tests, storybook-ish renders);
   * the baked-in fallback map is then used, matching the server's own fallback.
   */
  facetMapping?: Record<string, string>;
  /** PLP list id (category slug / "search") + site id, for the Segment
   *  `Product List Filtered` event. When omitted (e.g. unit tests that don't
   *  wire analytics) the event is skipped. */
  listId?: string;
  siteId?: string;
}

// Segment "Product List Filtered" payload builders (Segment ecommerce spec).
const facetsToFilters = (selected: SelectedCioFacets) =>
  Object.entries(selected).flatMap(([type, values]) =>
    values.map(v => ({ type, value: v.value })),
  );

const sortParamToSorts = (params: URLSearchParams) => {
  const sort = params.get("sort");
  if (!sort) return [];
  // The sort param is `${sort_by}-${sort_order}`; split on the LAST dash so a
  // hyphenated sort_by survives.
  const i = sort.lastIndexOf("-");
  return i > 0
    ? [{ type: sort.slice(0, i), value: sort.slice(i + 1) }]
    : [{ type: sort, value: "" }];
};

export function CioFacetProvider({
  facets,
  sortOptions,
  totalResults,
  children,
  listId,
  siteId,
  facetMapping,
}: CioFacetProviderProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Re-derive the lookups (inverted mapping + allowlist Set) from the plain
  // record the loader sent. React Router deserializes loader data fresh on
  // every navigation, so `facetMapping` is a NEW object reference each time
  // even when BM's value hasn't changed — memoising on that reference would
  // rebuild this on every navigation. Memoise on its serialized content
  // instead, so the rebuild only happens when the BM value actually changes.
  const facetMappingKey = facetMapping ? JSON.stringify(facetMapping) : "";
  const facetMappingConfig = useMemo(
    () =>
      facetMapping
        ? createFacetMappingConfig(facetMapping)
        : FALLBACK_FACET_MAPPING_CONFIG,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- facetMappingKey is the content-stable proxy for facetMapping, see comment above
    [facetMappingKey],
  );

  // Commit a params update via navigate (not setSearchParams): RR serializes
  // setSearchParams through `URLSearchParams.toString()`, which percent-encodes
  // `,` → `%2C`. navigate with a literal-comma query string keeps the comma, so
  // multi-value facet URLs stay byte-identical to prod (SFRA does the same:
  // `qs.replace(/%2C/g, ',')` + pushState). Mirrors SFRA's `pathname + '?' + qs`;
  // an empty query (all facets cleared, no preserved non-facet params) → bare
  // pathname with no trailing `?`. (SFCC-11657)
  const navigateWithParams = useCallback(
    (next: URLSearchParams) => {
      const qs = serializeSearchParams(next);
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
        preventScrollReset: true,
      });
    },
    [navigate, location.pathname],
  );

  // Derive selected facets from URL params
  const selectedCioFacets = useMemo((): SelectedCioFacets => {
    const selected: SelectedCioFacets = {};

    searchParams.forEach((value, key) => {
      // `price` is driven solely by pmin/pmax (handled below); isCioFacetParam
      // excludes it so a raw `?price=` never becomes a stray selected-facet pill.
      if (!isCioFacetParam(key, facetMappingConfig)) return;

      // The URL param may be a legacy alias (`product-type`); key the selected
      // state by the CIO facet name (`productType`) so the drawer, filter-tag
      // pills, and CIO request all stay on one name regardless of which inbound
      // form the URL carried (SFCC-11638).
      const cioName = urlParamToCioFacet(key);

      // Each param is comma-separated values: ?brand=aquaphor,caring-mill
      const values = value.split(",").filter(Boolean);
      if (values.length > 0) {
        // Find the matching facet to get display names
        const facet = facets.find(f => f.name === cioName);
        selected[cioName] = values.map(v => {
          const option = facet?.options.find(opt => opt.value === v);
          return {
            value: v,
            // A URL-selected value CIO no longer returns (renamed/discontinued
            // facet value) has no matching option. Title-case the raw slug so
            // its active-filter pill reads like the present, merchandised
            // values ("Menstrual Relief", not "menstrual-relief") instead of an
            // ugly slug — matching SFRA's SecondaryRefinementBar fallback
            // (SFCC-11627). Present values still use CIO's display_name.
            //
            // Deliberately UNSCOPED (all facets), unlike the loader's
            // normalizeCategoryFacetDisplayNames which scopes to category
            // facets to avoid corrupting merchandised casing like "84 ct."
            // (SFCC-11411). Safe here because this branch only fires for a
            // value CIO no longer returns — there is no merchandised name to
            // preserve, so title-casing a stale slug can't corrupt one.
            display_name: option?.display_name ?? humanizeCategoryId(v),
          };
        });
      }
    });

    // Handle price range from pmin/pmax params
    const pmin = searchParams.get("pmin");
    const pmax = searchParams.get("pmax");
    if (pmin || pmax) {
      const minStr = pmin || "-inf";
      const maxStr = pmax || "inf";
      selected.price = [
        {
          value: `${minStr} - ${maxStr}`,
          display_name: `${minStr} - ${maxStr}`,
        },
      ];
    }

    return selected;
  }, [searchParams, facets, facetMappingConfig]);

  // Derive selected sort option from URL
  const selectedSortOption = useMemo((): SortOption | null => {
    const sort = searchParams.get("sort");
    if (!sort) {
      // Return the one marked "selected" by CIO, or null
      return sortOptions.find(o => o.status === "selected") ?? null;
    }
    // Convention: sort param is "{sort_by}-{sort_order}"
    return (
      sortOptions.find(o => `${o.sort_by}-${o.sort_order}` === sort) ?? null
    );
  }, [searchParams, sortOptions]);

  // Mark status on sort options for UI (the "selected" indicator)
  const sortOptionsWithStatus = useMemo((): SortOption[] => {
    const sortParam = searchParams.get("sort");
    return sortOptions.map(opt => {
      const optKey = `${opt.sort_by}-${opt.sort_order}`;
      const isSelected = sortParam
        ? optKey === sortParam
        : opt.status === "selected";
      return { ...opt, status: isSelected ? "selected" : "" };
    });
  }, [searchParams, sortOptions]);

  // Build state object matching the original CioFacetState shape
  const state = useMemo(
    (): CioFacetState => ({
      customSitePreferences: {},
      facetMappingConfig,
      selectedCioFacets,
      cioFacets: facets,
      totalResults: {
        responseTotal: totalResults,
        selectedTotal: totalResults,
      },
      sortOptions: sortOptionsWithStatus,
      selectedSortOption,
      isInitialized: true,
    }),
    [
      facetMappingConfig,
      selectedCioFacets,
      facets,
      totalResults,
      sortOptionsWithStatus,
      selectedSortOption,
    ],
  );

  // Dispatch function that translates actions into URL param updates
  const dispatch = useCallback(
    (action: CioFacetAction) => {
      switch (action.type) {
        case SET_CIO_SELECTED_FACETS: {
          const next = new URLSearchParams(searchParams);

          // Remove existing facet params; price/pmin/pmax/page are cleared
          // explicitly below. Non-facet params (AFID, utm_*, q, sort,
          // cursor) are intentionally preserved.
          const keysToDelete: string[] = [];
          next.forEach((_, key) => {
            if (isCioFacetParam(key, facetMappingConfig)) {
              keysToDelete.push(key);
            }
          });
          keysToDelete.forEach(key => next.delete(key));

          // Remove price params — they'll be re-set if price facet exists.
          // `price` itself is excluded by isCioFacetParam, so clear it here.
          next.delete("price");
          next.delete("pmin");
          next.delete("pmax");

          // Set new facet params from payload
          const payload = action.payload as SelectedCioFacets;
          Object.entries(payload).forEach(([key, values]) => {
            if (key === "price" && values.length > 0) {
              // Price is stored as "min - max" in value
              const priceStr = values[0].value;
              const parts = priceStr.split(" - ");
              if (parts.length === 2) {
                const [min, max] = parts;
                if (min && min !== "-inf" && min !== "0") {
                  next.set("pmin", min);
                }
                if (max && max !== "inf") {
                  next.set("pmax", max);
                }
              }
            } else {
              const csvValues = values.map(v => v.value).join(",");
              if (csvValues) {
                // Emit the legacy alias (`productType` → `product-type`) so
                // the URL matches prod's SFRA `LegacyURLParamMapping`. The
                // delete loop above already cleared BOTH inbound forms via
                // isCioFacetParam, so no stale `productType` survives
                // (SFCC-11638).
                next.set(cioFacetToUrlParam(key), csvValues);
              }
            }
          });

          // Reset page on filter change
          next.delete("page");

          navigateWithParams(next);
          // Segment "Product List Filtered" (always-on sink) — noisy, one per
          // facet change. Skipped without siteId (unit tests don't wire analytics).
          if (siteId) {
            trackPageEvent(
              "Product List Filtered",
              {
                list_id: listId ?? "",
                filters: facetsToFilters(payload),
                sorts: sortParamToSorts(searchParams),
              },
              siteId,
            );
          }
          break;
        }

        case SET_SELECTED_SORT_OPTION: {
          const next = new URLSearchParams(searchParams);
          const sortOpt = action.payload;
          if (sortOpt && sortOpt.sort_by) {
            next.set("sort", `${sortOpt.sort_by}-${sortOpt.sort_order}`);
          } else {
            next.delete("sort");
          }
          // Reset page on sort change
          next.delete("page");
          navigateWithParams(next);
          // Segment "Product List Filtered" (sort variant) — current filter
          // state + the new sort. Skipped without siteId.
          if (siteId) {
            trackPageEvent(
              "Product List Filtered",
              {
                list_id: listId ?? "",
                filters: facetsToFilters(selectedCioFacets),
                sorts: sortOpt
                  ? [{ type: sortOpt.sort_by, value: sortOpt.sort_order }]
                  : [],
              },
              siteId,
            );
          }
          break;
        }

        case RESET_CIO_FACETS: {
          const next = new URLSearchParams(searchParams);
          // Clear every managed facet param; preserve non-facet params
          // (AFID, utm_*, q, sort, cursor). price/pmin/pmax/page explicit.
          const keysToDelete: string[] = [];
          next.forEach((_, key) => {
            if (isCioFacetParam(key, facetMappingConfig)) {
              keysToDelete.push(key);
            }
          });
          keysToDelete.forEach(key => next.delete(key));
          next.delete("price");
          next.delete("pmin");
          next.delete("pmax");
          next.delete("page");
          navigateWithParams(next);
          break;
        }

        // These actions are no-ops in the URL-driven adapter
        case SET_CIO_FACETS:
        case SET_SORT_OPTIONS:
        case SET_CUSTOM_SITE_PREFERENCES:
        case SET_CIO_FACETS_INITIALIZED:
          break;
      }
    },
    [
      searchParams,
      navigateWithParams,
      siteId,
      listId,
      selectedCioFacets,
      facetMappingConfig,
    ],
  );

  return (
    <CioFacetContext.Provider value={{ state, dispatch }}>
      {children}
    </CioFacetContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Selector hook (matching hec-composable interface)
// ---------------------------------------------------------------------------

/**
 * useCioFacetSelector — matches the original hec-composable interface.
 *
 * Usage:
 *   const { cioFacets, selectedCioFacets } = useCioFacetSelector(ctx => ctx.state)
 *   const dispatch = useCioFacetSelector(ctx => ctx.dispatch)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCioFacetSelector<T>(
  selector: (context: {
    state: CioFacetState;
    dispatch: Dispatch<CioFacetAction>;
  }) => T,
): T {
  const context = React.useContext(CioFacetContext);
  if (!context) {
    throw new Error(
      "useCioFacetSelector must be used within a CioFacetProvider",
    );
  }
  return selector(context);
}
